<script lang="ts">
	import {
		Button,
		Col,
		Icon,
		Input,
		Row,
		Select,
		Stack,
		Toggle,
		Tooltip,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { requestPresenceTiltCalibrate } from '../../helpers/requests';
	import {
		validateZoneDetectionHeights,
		formatZoneDetectionHeightErrors,
		detectionHeightToastTitle,
		DETECTION_HEIGHT_MAX_M,
		maxDetectionHeightInputMinBound,
		minDetectionHeightInputMaxBound,
	} from '../../helpers/radar-helper';

	const dispatch = createEventDispatcher();

	export let device: Device;
	export let settings: any = {};

	let tiltCalibrating = false;

	$: sensorTiltDisplay =
		settings.presence?.sensor?.tilt != null &&
		settings.presence?.sensor?.tilt !== '' &&
		!Number.isNaN(Number(settings.presence.sensor.tilt))
			? `${Number(settings.presence.sensor.tilt).toFixed(2)}°`
			: '';

	$: transmissionPowerOptions = [
		{ title: $format('presence_config_power_low', { default: 'Low' }), value: 'low' },
		{ title: $format('presence_config_power_medium', { default: 'Medium' }), value: 'medium' },
		{ title: $format('presence_config_power_high', { default: 'High' }), value: 'high' },
	];

	$: sensitivityOptions = [
		{ title: $format('presence_config_power_low', { default: 'Low' }), value: 'low' },
		{ title: $format('presence_config_power_medium', { default: 'Medium' }), value: 'medium' },
		{ title: $format('presence_config_power_high', { default: 'High' }), value: 'high' },
	];

	const getSelectOption = (e: CustomEvent | TouchEvent): string => {
		if (typeof e.detail === 'string') return e.detail;
		if (e.detail?.value) return e.detail.value as string;
		return 'medium';
	};

	$: selectedPowerOption = transmissionPowerOptions.find(
		(opt) => opt.value === settings.presence?.sensor?.power,
	);

	$: selectedSensitivityOption = sensitivityOptions.find(
		(opt) => opt.value === settings.presence?.sensor?.sensitivity,
	);

	let enable = settings.presence?.enable ?? true;
	let isFlipped = settings.presence?.sensor?.flipped ?? false;
	let imperial = settings.presence?.ui?.imperial ?? false;
	let configMinHeight = settings.presence?.zmin ?? 0;
	let configMaxHeight = settings.presence?.zmax ?? 3;
	let configSensorHeight = settings.presence?.sensor?.height ?? 2;
	let power = settings.presence?.sensor?.power ?? 'high';
	let sensitivity = settings.presence?.sensor?.sensitivity ?? 'medium';

	$: parsedConfigMax = Number(configMaxHeight);
	$: parsedConfigMin = Number(configMinHeight);
	$: configMinHeightInputMax = minDetectionHeightInputMaxBound(parsedConfigMax);
	$: configMaxHeightInputMin = maxDetectionHeightInputMinBound(parsedConfigMin);

	function handleDiscard() {
		enable = settings.presence?.enable ?? true;
		isFlipped = settings.presence?.sensor?.flipped ?? false;
		imperial = settings.presence?.ui?.imperial ?? false;
		configMinHeight = settings.presence?.zmin ?? 0;
		configMaxHeight = settings.presence?.zmax ?? 3;
		configSensorHeight = settings.presence?.sensor?.height ?? 2;
		power = settings.presence?.sensor?.power ?? 'high';
		sensitivity = settings.presence?.sensor?.sensitivity ?? 'medium';
	}

	function handleSave() {
		const raw = validateZoneDetectionHeights(String(configMinHeight), String(configMaxHeight));
		const { minError, maxError } = formatZoneDetectionHeightErrors(raw, get(format));
		if (minError || maxError) {
			const _format = get(format);
			toast.open({
				type: 'danger',
				title:
					detectionHeightToastTitle(minError, maxError) ||
					_format('presence_height_error_invalid_generic', {
						default: 'Check min and max detection height.',
					}),
			});
			return;
		}

		dispatch('configSave', {
			enable,
			zmin: Number(configMinHeight),
			zmax: Number(configMaxHeight),
			sensor: {
				height: Number(configSensorHeight),
				flipped: isFlipped,
				power,
				sensitivity,
			},
			ui: {
				imperial,
			},
		});
	}

	async function handleTiltCalibrate() {
		if (!device || tiltCalibrating) return;
		tiltCalibrating = true;
		try {
			const result = await requestPresenceTiltCalibrate(device);
			if (result?.isok) {
				toast.open({
					type: 'success',
					title: $format('presence_toast_tilt_calibrate_success', {
						default: 'Tilt calibrated successfully.',
					}),
				});
			} else {
				toast.open({
					type: 'danger',
					title: $format('presence_toast_tilt_calibrate_error', {
						default: 'Tilt calibration failed.',
					}),
				});
			}
		} finally {
			tiltCalibrating = false;
		}
	}
</script>

<Stack gap="s" class="mt-s">
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_enable_detection_label', { default: 'Enable detection' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_enable_detection_tooltip', {
						default: 'Activate presence detection',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Toggle
				aria-label={$format('toggle_enable_detection', { default: 'Enable detection' })}
				bind:checked={enable}
				size="m"
				id="toggle-enable-detection"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_flipped_label', { default: 'Flipped mounting' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_flipped_tooltip', {
						default: 'Enable if the device is mounted upside down (rotated 180°)',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Toggle
				aria-label={$format('toggle_flipped_mounting', { default: 'Flipped mounting' })}
				bind:checked={isFlipped}
				size="m"
				id="toggle-flipped-mounting"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_imperial_label', { default: 'Enable imperial units' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_imperial_tooltip', {
						default: 'Display metric and imperial units',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Toggle
				aria-label={$format('toggle_imperial_units', { default: 'Enable imperial units' })}
				bind:checked={imperial}
				size="m"
				id="toggle-imperial-units"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_min_height_label', {
						default: 'Min detection height (m)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_min_height_tooltip', {
						default:
							'Set the minimum height above the floor at which presence can be detected. Allowed range: 0 m up to (max detection height − 0.1 m).',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="0"
				max={configMinHeightInputMax}
				step="0.01"
				class="text-left"
				bind:value={configMinHeight}
				label={$format('presence_config_min_height_label', {
					default: 'Min detection height (m)',
				})}
				id="input-min-height"
				aria-labelledby="input-min-height"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_max_height_label', {
						default: 'Max detection height (m)',
					})}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_max_height_tooltip', {
						default:
							'Set the maximum height above the floor at which presence can be detected. Allowed range: (min detection height + 0.1 m) up to 5 m.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min={configMaxHeightInputMin}
				max={DETECTION_HEIGHT_MAX_M}
				step="0.01"
				bind:value={configMaxHeight}
				class="text-left"
				label={$format('presence_config_max_height_label', {
					default: 'Max detection height (m)',
				})}
				id="input-max-height"
				aria-labelledby="input-max-height"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_sensor_height_label', { default: 'Sensor height (m)' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_sensor_height_tooltip', {
						default: 'Set the height of the sensor relative to the floor. Range: 0 to 5 m.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Input
				type="number"
				min="0"
				max={DETECTION_HEIGHT_MAX_M}
				step="0.01"
				bind:value={configSensorHeight}
				label={$format('presence_config_sensor_height_label', { default: 'Sensor height (m)' })}
				id="input-sensor-height"
				aria-labelledby="input-sensor-height"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_sensor_tilt_label', { default: 'Sensor tilt' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_sensor_tilt_tooltip', {
						default:
							'Current tilt angle reported by the sensor. Use Calibrate to reset tilt measurement after mounting.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<div class="tilt-field">
				<div class="tilt-field__input">
					<Input
						type="text"
						readonly
						disabled
						value={sensorTiltDisplay}
						label={$format('presence_config_sensor_tilt_label', { default: 'Sensor tilt' })}
						id="input-sensor-tilt"
						aria-labelledby="input-sensor-tilt"
					/>
				</div>
				<div class="tilt-field__action">
					<Button
						variant="secondary"
						size="m"
						disabled={tiltCalibrating || !device}
						on:click={handleTiltCalibrate}
					>
						{$format('button_calibrate', { default: 'Calibrate' })}
					</Button>
				</div>
			</div>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_power_label', { default: 'Transmission power' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_power_tooltip', {
						default: 'Set the transmission power of the radar sensor.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Select
				aria-labelledby="select-power"
				icon={null}
				options={transmissionPowerOptions}
				label={$format('presence_config_power_label', { default: 'Transmission power' })}
				value={selectedPowerOption}
				on:change={(e) => (power = getSelectOption(e))}
				id="select-power"
			/>
		</Col>
	</Row>
	<Row>
		<Col>
			<div class="headline-with-info">
				<p role="presentation" class="mb-s body-styles">
					{$format('presence_config_sensitivity_label', { default: 'Detection sensitivity' })}
				</p>
				<Tooltip
					placement="top"
					title={$format('presence_config_sensitivity_tooltip', {
						default:
							'Set the detection sensitivity level. High sensitivity improves detection of stationary objects; low sensitivity reduces false triggers.',
					})}
				>
					<Icon icon="fas fa-info-circle" size="xs" class="text-color-muted ml-xs" />
				</Tooltip>
			</div>
			<Select
				aria-labelledby="select-sensitivity"
				icon={null}
				options={sensitivityOptions}
				label={$format('presence_config_sensitivity_label', {
					default: 'Detection sensitivity',
				})}
				value={selectedSensitivityOption}
				on:change={(e) => (sensitivity = getSelectOption(e))}
				id="select-sensitivity"
			/>
		</Col>
	</Row>
	<hr class="mt-l mb-s" />
	<Row>
		<Col>
			<div class="config-actions">
				<Button variant="secondary" size="m" on:click={handleDiscard}>
					{$format('button_discard_changes', { default: 'Discard changes' })}
				</Button>
				<Button variant="primary" size="m" on:click={handleSave}>
					{$format('button_save_settings', { default: 'Save settings' })}
				</Button>
			</div>
		</Col>
	</Row>
</Stack>

<style>
	.config-actions {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
	}

	.headline-with-info {
		text-align: left;
		display: flex;
		align-content: space-between;
		align-items: baseline;
		gap: 4px;
		margin-bottom: 0.5rem;
	}

	.headline-with-info p {
		margin: 0;
	}

	.tilt-field {
		display: flex;
		align-items: flex-end;
		gap: 12px;
	}

	.tilt-field__input {
		flex: 1;
		min-width: 0;
	}

	.tilt-field__action {
		flex-shrink: 0;
	}
</style>
